import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('data-card-model')) {
      const loadCardModel = () => {
        this.loadContent(false);
        this.#hideCardModelUntilReady();
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadCardModel, { timeout: 2000 });
      } else {
        setTimeout(loadCardModel, 0);
      }
    }
  }

  loadContent(focus = true) {
    super.loadContent(focus);

    if (this.hasAttribute('data-card-model')) {
      // Don't auto-play — rotation starts on hover via product-card.js
      return;
    }

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
    if (this.hasAttribute('data-card-model')) {
      const modelViewer = this.querySelector('model-viewer');
      if (typeof modelViewer?.pause === 'function') {
        modelViewer.pause();
      }
      modelViewer?.removeAttribute('auto-rotate');
    }
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
    if (this.hasAttribute('data-card-model')) {
      const modelViewer = this.querySelector('model-viewer');
      if (modelViewer) {
        this.#whenCardModelReady(modelViewer, () => {
          if (!this.isPlaying) return;

          this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
          this.#playCardModel(modelViewer);
        });
      }
    }
  }

  /**
   * Starts auto-rotation for product card model previews.
   * @param {Element} modelViewer
   */
  #playCardModel(modelViewer) {
    // Disable camera controls for card previews: keeps the cursor as the
    // default pointer (no grab/drag affordance) and stops scroll zooming
    // while the model auto-rotates on hover.
    modelViewer.removeAttribute('camera-controls');
    modelViewer.setAttribute('auto-rotate', '');
    modelViewer.setAttribute('auto-rotate-delay', '0');
    modelViewer.setAttribute('rotation-per-second', '30deg');

    if (typeof modelViewer.dismissPoster === 'function') {
      modelViewer.dismissPoster();
    }

    if (typeof modelViewer.play === 'function') {
      modelViewer.play();
      return;
    }

    customElements.whenDefined('model-viewer').then(() => {
      if (!this.isConnected || !this.isPlaying) return;

      if (typeof modelViewer.dismissPoster === 'function') {
        modelViewer.dismissPoster();
      }

      if (typeof modelViewer.play === 'function') {
        modelViewer.play();
      }
    });
  }

  #dismissCardModelPoster() {
    const modelViewer = this.querySelector('model-viewer');
    if (!modelViewer) return;

    if (typeof modelViewer.dismissPoster === 'function') {
      modelViewer.dismissPoster();
      return;
    }

    customElements.whenDefined('model-viewer').then(() => {
      if (!this.isConnected) return;

      if (typeof modelViewer.dismissPoster === 'function') {
        modelViewer.dismissPoster();
      }
    });
  }

  #hideCardModelUntilReady() {
    this.refs.deferredMediaPlayButton?.classList.remove('deferred-media__playing');
    this.removeAttribute('data-card-model-ready');
    this.isPlaying = false;

    const modelViewer = this.querySelector('model-viewer');
    if (!modelViewer) return;

    this.#whenCardModelReady(modelViewer, () => {
      this.#dismissCardModelPoster();
    });
  }

  /**
   * Keeps the preview image visible until model-viewer has rendered the GLB.
   * @param {Element} modelViewer
   * @param {() => void} callback
   */
  #whenCardModelReady(modelViewer, callback) {
    const safeCallback = () => {
      if (!this.isConnected) return;

      if (this.hasAttribute('data-card-model')) {
        this.setAttribute('data-card-model-ready', '');
      }

      callback();
    };

    const runWhenReady = () => {
      if (!this.isConnected) return;

      if ('loaded' in modelViewer && modelViewer.loaded) {
        safeCallback();
        return;
      }

      modelViewer.addEventListener('load', safeCallback, { once: true });
    };

    if (customElements.get('model-viewer')) {
      runWhenReady();
      return;
    }

    customElements.whenDefined('model-viewer').then(runWhenReady);
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    // In card context the model is revealed on hover without the UI controls.
    // Rotation is started/stopped by product-card.js on pointer enter/leave.
    if (this.hasAttribute('data-card-model')) {
      return;
    }

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
